import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Virtuoso } from "react-virtuoso";
import theme from "../LayoutComponents/theme";
import LoadingCircle from "../LayoutComponents/LoadingCircle";

const styles = {
  skillItem: {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem 0",
  },
  filterContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "1rem",
    width: "200px",
  },
  title: {
    color: theme.colors.purple,
  },
};

function SelectSkills({
  competencies,
  proficiencies,
  selected,
  setSelected,
  label = "Select ",
  loading,
  error,
}) {
  const [compTypeFilter, setCompTypeFilter] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const themeSizing = useTheme();
  const isMobile = useMediaQuery(themeSizing.breakpoints.down("sm"));
  const hasNormalized = useRef(false);

  // Normalize selected skills
  useEffect(() => {
    if (hasNormalized.current || !selected?.length) return;
    const normalized = selected.map((item) => {
      const comp = competencies.find((c) => c.compId === item.compId);
      const prof = proficiencies.find((p) => p.profRating === item.profRating);
      return {
        xWalkId: item.xWalkId ?? 0,
        impact: item.impact ?? "low",
        compId: item.compId,
        tierName: item.tierName || comp?.tierName || "",
        keyBehaviorId: comp?.keyBehaviorId,
        keyBehaviorName: comp?.keyBehaviorName,
        profRating: prof?.profRating ?? 1,
      };
    });
    setSelected(normalized);
    hasNormalized.current = true;
  }, [competencies, proficiencies, selected, setSelected]);

  // Handle individual competency toggle
  const handleCompetencyChange = useCallback(
    (compId) => {
      const comp = competencies.find((c) => c.compId === compId);
      setSelected((prev) => {
        const exists = prev.find((s) => s.compId === compId);
        if (exists) return prev.filter((s) => s.compId !== compId);
        return [
          ...prev,
          {
            xWalkId: 0,
            impact: "low",
            compId: comp.compId,
            tierName: comp.tierName,
            keyBehaviorId: comp.keyBehaviorId,
            keyBehaviorName: comp.keyBehaviorName,
            profRating: 1,
          },
        ];
      });
    },
    [competencies, setSelected]
  );

  // Handle proficiency change
  const handleProficiencyChange = useCallback(
    (compId, profRating) => {
      setSelected((prev) =>
        prev.map((s) =>
          s.compId === compId ? { ...s, profRating } : s
        )
      );
    },
    [setSelected]
  );

  // Group competencies by key behavior, filtered by tier
  const groupedCompetencies = useMemo(() => {
    const filtered = competencies.filter(
      (c) => !compTypeFilter || c.tierName === compTypeFilter
    );
    const groups = new Map();
    filtered.forEach((c) => {
      if (!groups.has(c.keyBehaviorId)) {
        groups.set(c.keyBehaviorId, {
          keyBehaviorName: c.keyBehaviorName,
          comps: [],
        });
      }
      groups.get(c.keyBehaviorId).comps.push(c);
    });
    return Array.from(groups.values());
  }, [competencies, compTypeFilter]);

  const uniqueCompTypes = useMemo(
    () => [...new Set(competencies.map((c) => c.tierName))].sort(),
    [competencies]
  );

  // Toggle group expansion
  const toggleGroup = useCallback((keyBehaviorId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(keyBehaviorId)) {
        newSet.delete(keyBehaviorId);
      } else {
        newSet.add(keyBehaviorId);
      }
      return newSet;
    });
  }, []);

  // Render item for Virtuoso
  const renderGroup = (index) => {
    const group = groupedCompetencies[index];
    const keyBehaviorId = group.comps[0]?.keyBehaviorId;
    const isExpanded = expandedGroups.has(keyBehaviorId);
    const allSelected = group.comps.every((c) =>
      selected?.some((s) => s.compId === c.compId)
    );
    const someSelected = group.comps.some((c) =>
      selected?.some((s) => s.compId === c.compId)
    );

    const toggleAllGroup = (checked) => {
      if (checked) {
        const newComps = group.comps
          .filter((c) => !selected?.some((s) => s.compId === c.compId))
          .map((c) => ({
            xWalkId: 0,
            impact: "low",
            compId: c.compId,
            tierName: c.tierName,
            keyBehaviorId: c.keyBehaviorId,
            keyBehaviorName: c.keyBehaviorName,
            profRating: 1,
          }));
        setSelected([...selected, ...newComps]);
      } else {
        setSelected(selected.filter((s) => !group.comps.some((c) => c.compId === s.compId)));
      }
    };

    return (
      <Box key={group.keyBehaviorName} sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            size="small"
            onClick={() => toggleGroup(keyBehaviorId)}
            sx={{ 
              color: theme.colors.purple,
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMore />
          </IconButton>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => toggleAllGroup(e.target.checked)}
            sx={{
              color: theme.colors.purple,
              "&.Mui-checked": { color: theme.colors.purple },
              "&.MuiCheckbox-indeterminate": { color: theme.colors.purple },
            }}
          />
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: theme.typography.fontWeightBold, 
              color: theme.colors.purple,
              cursor: 'pointer',
            }}
            onClick={() => toggleGroup(keyBehaviorId)}
          >
            {group.keyBehaviorName}
          </Typography>
        </Box>
        {isExpanded && group.comps.map((competency) => {
          const selectedSkill = selected?.find((s) => s.compId === competency.compId);
          return (
            <Box
              key={competency.compId}
              sx={{ ...styles.skillItem, pl: 8 }}
            >
              <Checkbox
                checked={Boolean(selectedSkill)}
                onChange={() => handleCompetencyChange(competency.compId)}
                sx={{
                  color: theme.colors.purple,
                  "&.Mui-checked": { color: theme.colors.purple },
                }}
              />
              <Typography flex={1}>{competency.compDesc}</Typography>
              <FormControl sx={{ width: isMobile ? "50%" : "25%" }} size="small">
                <InputLabel>Proficiency</InputLabel>
                <Select
                  value={selectedSkill?.profRating || 1}
                  onChange={(e) => handleProficiencyChange(competency.compId, e.target.value)}
                  disabled={!selectedSkill}
                >
                  {proficiencies.map(({ profRating, profDescription }) => (
                    <MenuItem key={profRating} value={profRating}>
                      {profDescription.split("(")[0].trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          );
        })}
        <Divider sx={{ mt: 1 }} />
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, display: "flex", flexDirection: "column", height: "60vh" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h4" sx={styles.title}>{label + " Skills"}</Typography>
        {error && <Typography color="red" fontWeight="bold">Error on Form</Typography>}
        {loading && <LoadingCircle size={20} />}
      </Box>

      <Box sx={{ ...styles.filterContainer, width: isMobile ? "100%" : "200px" }}>
        <FormControl fullWidth>
          <InputLabel>Competency Type</InputLabel>
          <Select
            value={compTypeFilter}
            onChange={(e) => setCompTypeFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueCompTypes.map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Virtuoso
          totalCount={groupedCompetencies.length}
          itemContent={renderGroup}
          style={{ height: "100%" }}
        />
      </Box>
    </Paper>
  );
}

export default React.memo(SelectSkills);
